import style from './index.module.less';
import { useEffect, useRef, useState } from 'react';
import { WhatsNewApi } from '@Apis';
import i18next from 'i18next';
import { SHA256 } from 'crypto-js';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Modal,
  Image,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { isInUIC } from '../../utils/common';
import { IS_UAE } from '@Utils/const';
import addaLogoMini from '../../assets/image/white-label/cyber-eye.svg';
import logoMini from '../../assets/image/logo-mini.svg';
import { checkForShowUpgradeModal } from '../Banner/UpgradeBanner/shared';
import { useGlobalSettingStore } from '../../models/useGlobalSettingModel';

export const WhatsNew = () => {
  // must have an additional state to trigger the modal
  // if need show upgrade popup, whats new modal must be shown after upgrade popup showed
  // This is a very strange code logic...
  const [checkForPopup, setCheckForPopup] = useState(true);
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();

  useEffect(() => {
    const listener = () => {
      setCheckForPopup(false);
    };
    window.addEventListener('showWhatsNewModal', listener);
    return () => {
      window.removeEventListener('showWhatsNewModal', listener);
    };
  }, []);

  if (isInUIC()) {
    return <WhatsNewModal isOpen={false} />;
  }

  if (
    checkForShowUpgradeModal() &&
    selectedOrgGlobalSetting.showPromotionPopupWindow &&
    checkForPopup
  ) {
    // don't return <></>
    // maybe cause problems of mount/unmount
    // cause localStorage for 'hasWhatsNewShowed' to exist earlier
    return <WhatsNewModal isOpen={false} />;
  }

  return <WhatsNewModal isOpen={true} />;
};

const WhatsNewModal = (props: { isOpen: boolean }) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation('common');
  const [whatsNewSections, setWhatsNewSections] =
    useState<HTMLCollectionOf<HTMLElement>>();
  const [isOpen, setOpen] = useState(props.isOpen);

  useEffect(() => {
    setOpen(props.isOpen);
  }, [props.isOpen]);

  const checkboxRef = useRef<HTMLInputElement>();

  const [preference, setPreference] = useState({
    whatsNewDisabled: false,
    olhVersion: 'unknownVersion',
  });

  const currentWhatsNewVersionRef = useRef('');

  useEffect(() => {
    if (localStorage.getItem('hasWhatsNewShowed') === 'true') {
      return;
    }

    (async () => {
      try {
        const preferences = (await WhatsNewApi.apiGetWhatsNewPreferences())
          .data;
        const whatsNewContent = await WhatsNewApi.getWhatsNewContent(
          i18next.language
        );
        //the whats new version
        const currentWhatsNewVersion = SHA256(
          whatsNewContent as any
        ).toString();

        console.log('whatsNew version: ' + currentWhatsNewVersion);
        //if disabled and version is same, skip show whats new
        if (
          preferences.whatsNewDisabled &&
          currentWhatsNewVersion === preferences.olhVersion
        ) {
          localStorage.setItem('hasWhatsNewShowed', 'true');
          return;
        }

        const html = new DOMParser().parseFromString(
          (whatsNewContent as any) || '',
          'text/html'
        );
        const sections = html.getElementsByTagName('section');
        if (sections && sections.length === 3) {
          setWhatsNewSections(sections);
          setPreference(preferences);
          if (checkboxRef.current) {
            checkboxRef.current.checked = preferences.whatsNewDisabled;
          }
          currentWhatsNewVersionRef.current = currentWhatsNewVersion;
          localStorage.setItem('hasWhatsNewShowed', 'true');
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  if (!whatsNewSections || whatsNewSections.length !== 3) {
    return <></>;
  }

  const callbackFn = (e: Element, i: number, arr: Element[]) => {
    const isTitle = !!e.querySelector('b');
    // Fix: Ensure arr[i - 1] exists before accessing textContent
    const prevElement = i > 0 ? arr[i - 1] : null;
    const key = isTitle
      ? `${e.textContent}_title_${i}`
      : `${prevElement?.textContent ?? e.textContent ?? i}_desc_${i}`;

    return (
      <Box
        key={key}
        className={isTitle ? style.subTitle : style.content}
        color={isTitle ? colorStyle.color.primary : colorStyle.color.secondary}
      >
        <Text>{e.textContent}</Text>
      </Box>
    );
  };

  const whatsNewParagraphs = Array.from(
    whatsNewSections[1].querySelectorAll('.p')
  ).map(callbackFn);

  const comingSoonParagraphs = Array.from(
    whatsNewSections[2].querySelectorAll('.p')
  ).map(callbackFn);

  const borderStyle = {
    border: '1px solid',
    borderColor: colorStyle.background.tertiary,
  };

  const changeUserPreferences = () => {
    if (
      checkboxRef.current?.checked === preference.whatsNewDisabled &&
      currentWhatsNewVersionRef.current === preference.olhVersion
    ) {
      return;
    }

    // no need to wait
    WhatsNewApi.apiUpdateWhatsNewPreferences({
      whatsNewDisabled: (checkboxRef.current as HTMLInputElement).checked,
      olhVersion: currentWhatsNewVersionRef.current,
    });
  };

  return (
    <Modal
      id="whatsNew"
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={isOpen}
      onClose={() => setOpen(false)}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap="8px">
            <Image
              src={IS_UAE ? addaLogoMini : logoMini}
              height={28}
              mt="4px"
            />
            <Box
              dangerouslySetInnerHTML={{
                __html: IS_UAE
                  ? 'Cyber Eye Cloud Service Security'
                  : t('common.productName'),
              }}
            />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Grid templateColumns="1fr 1fr">
            <Box id="whatsNewTitle" className={style.title} {...borderStyle}>
              {whatsNewSections[1].querySelector('.section-title')?.textContent}
            </Box>
            <Box id="comingSoonTitle" className={style.title} {...borderStyle}>
              {whatsNewSections[2].querySelector('.section-title')?.textContent}
            </Box>
            <Box {...borderStyle} padding="16px 12px">
              {whatsNewParagraphs}
            </Box>
            <Box {...borderStyle} padding="16px 12px">
              {comingSoonParagraphs}
            </Box>
          </Grid>
          <Flex justifyContent="flex-end" mt="8px">
            <Text color={colorStyle.color.secondary}>
              {whatsNewSections[0].textContent}
            </Text>
          </Flex>
        </ModalBody>
        <ModalFooter gap="16px">
          <Checkbox {...{ inputRef: checkboxRef }}>
            {t('common.whatsNews.hide')}
          </Checkbox>
          <Button
            onClick={() => {
              changeUserPreferences();
              setOpen(false);
            }}
            variant="primary"
          >
            {t('common.action.label.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
